import {PageHeader, SaveCancelFooter} from '@/components/molecules';
import {TargetSetUp} from '@/containers';
import {useAppDispatch, useAppSelector} from '@/store';
import {useState, useEffect, useMemo} from 'react';
import {
  resetSalesTarget,
  setSalesTargetFormData
} from '@/store/slices/salesTargetSlice';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {ProgressIndicator, Breadcrumb} from '@/components/atoms';
import {useNavigate, useLocation} from 'react-router-dom';
import {ROUTES} from '@/utils/constants/routesConstants';
import {useSalesTargets} from '@/hooks/useSalesTargets';
import {TARGET_TYPES} from '@/utils/constants/saleTargetConstants';
import {Constants} from '@/utils/constants/commonConstants';
import {getModal} from '@/utils/CommonModal';
import {
  getStepConfig,
  isCdaSuperTargetType
} from '@/utils/salesTargetStepConfig';
import {logger} from '@/helpers/logger';

const toDateOrNull = (value) => (value ? new Date(value) : null);

const validateLinkedTarget = (linked, epStart, epEnd, awardDate) => {
  const linkedStart = toDateOrNull(linked.activeStartDate);
  const linkedEnd = toDateOrNull(linked.activeEndDate);

  if (
    linkedStart &&
    linkedEnd &&
    epStart &&
    epEnd &&
    (epStart < linkedStart || epEnd > linkedEnd)
  ) {
    return `Early Payment Target dates must fall within "${linked.name}" active date range.`;
  }

  if (linkedEnd && awardDate && awardDate <= linkedEnd) {
    return `Award Date must be after the Active End Date of "${linked.name}".`;
  }

  return null;
};

const getEarlyPaymentDateError = (formData) => {
  const epStart = toDateOrNull(formData.startDate);
  const epEnd = toDateOrNull(formData.endDate);
  const awardDate = toDateOrNull(formData.pointsDeliveryDate);

  for (const linked of formData.linkedSalesTargets) {
    const error = validateLinkedTarget(linked, epStart, epEnd, awardDate);
    if (error) return error;
  }
  return null;
};

const AddSalesTarget = () => {
  const location = useLocation();
  const isEdit = location?.state?.isEdit ?? false;
  const disableDraft = location?.state?.disableDraft ?? false;
  const salesTargetIdFromState = location?.state?.salesTargetId;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const salesTarget = useAppSelector((state) => state?.salesTarget);
  const [isCancel, setIsCancel] = useState(false);
  const [formData, setFormData] = useState(salesTarget?.formData);
  const [isPrerequisiteTarget, setIsPrerequisiteTarget] = useState(false);
  const [isLayeredTarget, setIsLayeredTarget] = useState(false);

  const isEarlyPaymentTarget = useMemo(
    () => formData?.targetType === TARGET_TYPES.EarlyPaymentTarget,
    [formData?.targetType]
  );

  const isDropSizeTarget = useMemo(
    () => formData?.targetType === TARGET_TYPES.DropSizeTarget,
    [formData?.targetType]
  );

  const isLinkedTargetType = useMemo(
    () => isEarlyPaymentTarget || isDropSizeTarget,
    [isEarlyPaymentTarget, isDropSizeTarget]
  );

  const isRackChallenge = useMemo(
    () => formData?.targetType === TARGET_TYPES.RackChallenge,
    [formData?.targetType]
  );

  const isProductGroupQuantity = useMemo(
    () => formData?.targetType === TARGET_TYPES.ProductGroupQuantity,
    [formData?.targetType]
  );

  const isCdaSuperTarget = useMemo(
    () => isCdaSuperTargetType(formData?.targetType),
    [formData?.targetType]
  );

  const isThreeStepTarget = useMemo(
    () => isLinkedTargetType || isRackChallenge || isCdaSuperTarget,
    [isLinkedTargetType, isRackChallenge, isCdaSuperTarget]
  );

  const stepConfig = useMemo(
    () => getStepConfig(formData?.targetType),
    [formData?.targetType]
  );
  const {
    handleDraft,
    handleNameValidation,
    isNameValid,
    setIsNameValid,
    handleValidationModalClose
  } = useSalesTargets(false);

  useEffect(() => {
    setIsPrerequisiteTarget(
      salesTarget.targetType === TARGET_TYPES.MonthlyTargetPrerequistite ||
        salesTarget.targetType === TARGET_TYPES.ProductTargetPrerequisite
    );
    setIsLayeredTarget(
      salesTarget?.targetType === TARGET_TYPES.ProductTargetLayered ||
        salesTarget?.targetType === TARGET_TYPES.MonthlyTargetLayered
    );
  }, [salesTarget.targetType]);

  const handleNextAction = async () => {
    // Temporary: Sales Target Conditions are not built yet, so CDA can move
    // to Display without a complete Setup.
    if (!isCdaSuperTarget) {
      const isValidName = await validateName();
      if (!isValidName) return;
    }

    // Early Payment Target and Drop Size Target date range validation against all linked targets
    if (isLinkedTargetType && formData?.linkedSalesTargets?.length > 0) {
      const dateError = getEarlyPaymentDateError(formData);
      if (dateError) {
        dispatch(
          setToastNotifications({show: true, message: dateError, type: 'error'})
        );
        return;
      }
    }

    const data = {...formData};
    data.displayStartDate = data?.activeDisplay
      ? data?.startDate
      : data?.displayStartDate;
    data.displayEndDate = data?.activeDisplay
      ? data?.endDate
      : data?.displayEndDate;
    dispatch(setSalesTargetFormData(data));

    const displayRoute = isRackChallenge
      ? ROUTES.RACK_CHALLENGE_DISPLAY_CONTENT
      : ROUTES.SALES_TARGET_DISPLAY_CONTENT;

    navigate(displayRoute, {
      state: {
        isEdit: isEdit,
        disableDraft: disableDraft,
        editPublishedBeforeStart: disableDraft,
        salesTargetId: salesTargetIdFromState || salesTarget?.id
      }
    });
  };

  const getNavigationState = () => ({
    isEdit,
    disableDraft,
    editPublishedBeforeStart: disableDraft,
    salesTargetId: salesTargetIdFromState || salesTarget?.id
  });

  const handleStepClick = (step) => {
    dispatch(setSalesTargetFormData({...formData}));

    const displayContentRoute = isRackChallenge
      ? ROUTES.RACK_CHALLENGE_DISPLAY_CONTENT
      : ROUTES.SALES_TARGET_DISPLAY_CONTENT;

    const stepRoutes = isThreeStepTarget
      ? {
          2: {route: displayContentRoute, withState: true},
          3: {route: ROUTES.SALES_TARGET_REVIEW, withState: false}
        }
      : {
          2: {route: ROUTES.SALES_TARGET_DISPLAY_CONTENT, withState: true},
          3: {route: ROUTES.SALES_TARGET_PRODUCTS, withState: true},
          4: {route: ROUTES.SALES_TARGET_REVIEW, withState: false}
        };

    const config = stepRoutes[step];
    if (config) {
      navigate(
        config.route,
        config.withState ? {state: getNavigationState()} : undefined
      );
    }
  };

  const validateName = async () => {
    const targetName = formData?.targetName;

    // Check if name is empty or whitespace only
    if (!targetName || String(targetName).trim() === '') {
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Target Name is mandatory',
          type: 'error'
        })
      );
      return false;
    }

    // In published-before-start edit flow we don't allow changing the name.
    // Skip duplicate-name validation in this scenario.
    // Also skip for In-Progress targets since we're only editing dates, not names.
    const isInProgressTarget =
      salesTarget?.status &&
      salesTarget.status.toLowerCase() === Constants.InProgress.toLowerCase();
    if (disableDraft || isInProgressTarget) {
      setIsNameValid(true);
      return true;
    }

    const id = salesTargetIdFromState ?? salesTarget?.id ?? null;
    const uuid = salesTarget?.uuid ?? null;
    const params = {
      id,
      uuid,
      name: targetName
    };
    if (params.id === 1) delete params.id;
    const resp = await handleNameValidation(params);
    const isValid = resp?.success === true;
    setIsNameValid(isValid);
    return isValid;
  };

  const {
    targetType,
    targetNameValid,
    startDateValid,
    endDateValid,
    activeDisplay,
    displayStartDateValid,
    displayEndDateValid,
    targetDeliveryDateValid,
    awardPointsImmedFlag,
    pointsDeliveryDateValid,
    roundUpPointsFlag,
    roundOffValueValid,
    pointsDeliveryDate,
    isNumberOfLevelsSelected,
    isNumberOfMilestonesSelected,
    pointDisplay
  } = formData;

  const handleDraftAction = async () => {
    // When editing, merge the updated formData with the existing salesTarget data
    // When creating new, use formData as it only contains step 1 data
    try {
      const isValidName = await validateName();
      if (!isValidName) return;
      const dataToSend = isEdit ? {...salesTarget, ...formData} : formData;
      const response = await handleDraft(
        dataToSend,
        salesTarget,
        isPrerequisiteTarget
      );
      if (response.success) {
        navigate(ROUTES.SALES_TARGETS);
      }
    } catch (error) {
      logger.error('Error saving draft:', error);
      return;
    }
  };

  const handleCancel = () => {
    setIsCancel(!isCancel);
  };
  const onClickSecondaryButton = () => {
    setIsCancel(!isCancel);
  };
  const onClickPrimaryButton = () => {
    dispatch(resetSalesTarget());
    navigate(ROUTES.SALES_TARGETS);
  };

  const validateFormData = (isValid) => {
    return isValid === undefined || isValid === true;
  };

  const isEmptyValue = (value) => {
    return !value || (typeof value === 'string' && value.trim() === '');
  };

  const earlyPaymentFieldsValidation = () => {
    const hasLinkedTarget = formData?.linkedSalesTargets?.length > 0;
    const shouldValidatePointsDelivery = hasLinkedTarget || isDropSizeTarget;

    return (
      !targetType ||
      validateFormData(targetNameValid) ||
      !formData?.startDate ||
      validateFormData(startDateValid) ||
      !formData?.endDate ||
      validateFormData(endDateValid) ||
      (shouldValidatePointsDelivery &&
        (!formData?.pointsDeliveryDate ||
          validateFormData(formData?.pointsDeliveryDateValid)))
    );
  };

  const commonFieldsValidation = () => {
    return (
      !targetType ||
      validateFormData(targetNameValid) ||
      !formData?.startDate ||
      validateFormData(startDateValid) ||
      !formData?.endDate ||
      validateFormData(endDateValid) ||
      !formData?.targetDeliveryDate ||
      validateFormData(targetDeliveryDateValid) ||
      validateFormData(isNumberOfLevelsSelected) ||
      validateFormData(isNumberOfMilestonesSelected) ||
      (isLayeredTarget && isEmptyValue(pointDisplay))
    );
  };

  const hasDisplayDateError = (hasCommonError) => {
    if (activeDisplay) return false;

    const hasError =
      hasCommonError ||
      validateFormData(displayStartDateValid) ||
      validateFormData(displayEndDateValid) ||
      !formData?.displayStartDate ||
      !formData?.displayEndDate;

    return hasError;
  };

  const hasPointsDeliveryDateError = (hasCommonError) => {
    if (awardPointsImmedFlag) return false;

    const hasError =
      hasCommonError ||
      !pointsDeliveryDate ||
      validateFormData(pointsDeliveryDateValid);

    return hasError;
  };

  const hasStoreListError = () => {
    if (isRackChallenge || isProductGroupQuantity) return false;
    const hasError = salesTarget?.storeList?.length === 0;

    return hasError;
  };

  const getRoundOffError = (hasCommonError) => {
    const roundOffError =
      hasCommonError || validateFormData(roundOffValueValid);

    return roundOffError;
  };

  const getFormError = () => {
    // Disable Next button for new Product Group Quantity targets
    if (isProductGroupQuantity && !isEdit) return true;

    // Temporary: do not block CDA Next while Sales Target Conditions are unfinished.
    if (isCdaSuperTarget) return false;

    if (isLinkedTargetType) {
      const hasEpError = earlyPaymentFieldsValidation();
      if (hasDisplayDateError(hasEpError)) return true;
      if (hasStoreListError()) return true;
      return hasEpError;
    }

    const hasCommonError = commonFieldsValidation();

    if (
      isRackChallenge &&
      (!formData?.challengeTransactionId ||
        validateFormData(formData?.challengeTransactionIdValid))
    )
      return true;

    if (hasDisplayDateError(hasCommonError)) return true;
    if (hasPointsDeliveryDateError(hasCommonError)) return true;
    if (hasStoreListError()) return true;
    if (roundUpPointsFlag) return getRoundOffError(hasCommonError);

    return hasCommonError;
  };

  // Disable Save Draft for new Product Group Quantity targets
  const shouldDisableDraft = useMemo(
    () => disableDraft || (isProductGroupQuantity && !isEdit),
    [disableDraft, isProductGroupQuantity, isEdit]
  );

  const getCustomModal = (type) => {
    switch (type) {
      case 'CANCEL_MODAL':
        return getModal({
          title: 'Are you sure you want to cancel?',
          text: 'Your work will not be saved if you click Yes. To continue editing and go back to the page, click NO',
          showPrimaryButton: true,
          primaryButtonText: 'Yes',
          onClickPrimaryButton: onClickPrimaryButton,
          showSecondaryButton: true,
          secondaryButtonText: 'No',
          onClickSecondaryButton: onClickSecondaryButton,
          showModal: isCancel,
          onCloseModal: handleCancel,
          hideCloseButton: false
        });
      case 'NAME_VALIDATION_MODAL':
        return getModal({
          title: 'Duplicate Name Detected',
          text: (
            <>
              It looks like there's already a Sales Target with this name.
              <br />
              To avoid confusion, please enter a unique name before continuing.
            </>
          ),
          showPrimaryButton: true,
          primaryButtonText: 'Edit Name',
          onClickPrimaryButton: handleValidationModalClose,
          showSecondaryButton: false,
          showModal: !isNameValid,
          onCloseModal: handleValidationModalClose,
          hideCloseButton: true
        });
      default:
        return null;
    }
  };
  return (
    <>
      <Breadcrumb
        uiSchema={{
          isDark: true,
          className: 'mb-5',
          breadcrumbs: [
            {
              href: ROUTES.HOME,
              label: ''
            },
            {
              href: ROUTES.SALES_TARGETS,
              label: 'Loyalty Campaigns'
            },
            {
              href: ROUTES.SALES_TARGETS,
              label: 'Add Sales Target'
            }
          ]
        }}
        id={'ast_bradcrumb'}
      />
      <PageHeader title={'Add Sales Target'} />

      <ProgressIndicator
        currentStep={1}
        completed={false}
        orientation='horizontal'
        steps={stepConfig.steps}
        titles={stepConfig.titles}
        className='mb-6'
        onStepClick={handleStepClick}
      />
      <br />
      <TargetSetUp
        setFormData={setFormData}
        formData={formData}
        salesTarget={salesTarget}
      />
      {isCancel && getCustomModal('CANCEL_MODAL')}
      {!isNameValid && getCustomModal('NAME_VALIDATION_MODAL')}
      <SaveCancelFooter
        FormError={getFormError()}
        handleCancel={handleCancel}
        draftAction={handleDraftAction}
        nextAction={handleNextAction}
        disableDraft={shouldDisableDraft}
        hideSaveDraft={isProductGroupQuantity && !isEdit}
      />
    </>
  );
};

export default AddSalesTarget;
